/**
 * Thin wrapper around @material/web's `<md-circular-progress>` — Google's
 * official M3 implementation (https://m3.material.io/components/progress-indicators).
 *
 * We pass through `value` / `indeterminate` directly and only set the two
 * Clavis defaults: size (44 px per Figma) and active color (brand #4D69B9).
 * Track color, stroke caps, animation, gap geometry — all left to Google's
 * implementation. Don't reinvent.
 */
import { useRef } from 'react';
// Side-effect import: registers <md-circular-progress> as a custom element.
// Kept here (not in main.tsx) so the @material/web payload only enters the
// bundle when a page actually imports ClavisCircularProgress.
import '@material/web/progress/circular-progress.js';
import type { MdCircularProgress } from '@material/web/progress/circular-progress.js';

export interface ClavisCircularProgressProps {
  /** Diameter in CSS pixels. Default 44 (Figma value). */
  size?: number;
  /** Active arc colour. Default #4D69B9 (Clavis brand). */
  activeColor?: string;
  /** 0–1 (determinate) value. Omit + pass `indeterminate` for a spinner. */
  value?: number;
  /** Spinner mode (M3's native indeterminate animation). */
  indeterminate?: boolean;
  /** Accessible label. When omitted, the element is `aria-hidden`. */
  ariaLabel?: string;
}

const ClavisCircularProgress: React.FC<ClavisCircularProgressProps> = ({
  size = 44,
  activeColor = '#4D69B9',
  value,
  indeterminate,
  ariaLabel,
}) => {
  const ref = useRef<MdCircularProgress | null>(null);
  return (
    <md-circular-progress
      ref={ref}
      value={value}
      indeterminate={indeterminate}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      style={{
        ['--md-circular-progress-size' as string]: `${size}px`,
        ['--md-circular-progress-active-indicator-color' as string]: activeColor,
      } as React.CSSProperties}
    />
  );
};

export default ClavisCircularProgress;
